import { createPublicKey, createVerify } from 'node:crypto';
import { proxyAwareFetch } from '../utils/aws-proxy.js';
import { getLogger } from '../utils/logger.js';
import type { CachedAuthorizerResult } from './authorizer-cache.js';
import type { CognitoUserPoolAuthorizer, JwtAuthorizer } from './authorizer-resolver.js';
import { buildIdentityHash } from './authorizer-resolver.js';
import { sanitizeServiceExceptionMessage } from './credential-error.js';

/**
 * Cognito User Pool / JWT authorizer support for `cdkl start-api`
 * (PR 8b).
 *
 * cdk-local verifies JWTs locally against the user pool's published JWKS so
 * the developer can exercise authorizer-protected routes with real-ish
 * tokens (e.g. ones minted by `aws cognito-idp admin-initiate-auth`).
 *
 * **JWKS-fetch failure handling** (locked design decision): when the
 * JWKS endpoint is unreachable at startup, we warn and fall back to a
 * pass-through mode where every JWT is accepted as if valid. Surprising
 * deny is worse than warn+allow for a dev tool. The warn line names the
 * unreachable URL so users can investigate (proxy, network, missing
 * pool) and is repeated on first request to the affected authorizer.
 *
 * Spec references:
 *   - Cognito JWT structure:
 *     https://docs.aws.amazon.com/cognito/latest/developerguide/amazon-cognito-user-pools-using-tokens-with-identity-providers.html
 *   - JWKS:
 *     https://docs.aws.amazon.com/cognito/latest/developerguide/amazon-cognito-user-pools-using-tokens-verifying-a-jwt.html
 */

interface JwksKey {
  /** Key id; matches the `kid` claim in the JWT header. */
  kid: string;
  /** RSA modulus, base64url-encoded. */
  n: string;
  /** RSA exponent, base64url-encoded (typically `AQAB`). */
  e: string;
  /** Algorithm — always `RS256` for Cognito. */
  alg?: string;
  /** Key type — `RSA`. */
  kty: string;
  /** Key use — `sig` for signing keys. */
  use?: string;
}

interface JwksCacheEntry {
  /** All keys keyed by `kid` for O(1) lookup. */
  byKid: Map<string, JwksKey>;
  /** When this entry expires (unix ms). 1hr from fetch by default. */
  expiresAt: number;
  /** True when the fetch failed and we're in pass-through mode. */
  passThrough: boolean;
}

/**
 * Cache of JWKS responses, keyed by the JWKS URL. cdk-local refreshes the
 * cache lazily on miss; entries live for 1hr by default (Cognito rotates
 * keys infrequently, so this is conservative).
 */
export interface JwksCache {
  fetchAndCache(jwksUrl: string): Promise<JwksCacheEntry>;
  /** Get the cached entry without refreshing — returns undefined on miss. */
  peek(jwksUrl: string): JwksCacheEntry | undefined;
  clear(): void;
}

/**
 * Per-time-window dedup map for "JWKS / OIDC discovery unreachable -> token
 * accepted without verification" warn lines.
 *
 * Pre-#247 the dedup was a `Set<string>` (URL -> warned-forever-for-this-run).
 * Once tripped, every subsequent JWT was silently accepted for the rest of
 * the session — a long-running `cdkl start-alb --watch` user briefly off VPN
 * at boot would never see the degraded-auth state again.
 *
 * Post-#247 the dedup is a `Map<string, number>` (URL -> last-warned-at unix
 * ms). The verifier re-emits the warn every {@link WARN_REEMIT_INTERVAL_MS}
 * so the user keeps seeing the degraded state surfaced periodically.
 *
 * Key shape: the URL of the unreachable endpoint (JWKS URL for the JWKS
 * fallback, discovery URL for the OIDC-discovery fallback). Distinct IdPs
 * therefore get independent dedup windows.
 */
export type WarnedAt = Map<string, number>;

/**
 * Re-emit window for unreachable-endpoint warns. 5 minutes is short enough
 * the user notices the degraded state on a normal `--watch` session, long
 * enough not to spam the log on every request when the upstream IdP is down.
 */
export const WARN_REEMIT_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Returns true iff `key`'s last warn timestamp is older than
 * {@link WARN_REEMIT_INTERVAL_MS} (or has never been recorded). When true,
 * `warnedAt[key]` is updated to `now` as a side effect so the next call
 * within the window returns false.
 *
 * Callers that omit `warnedAt` (the per-AuthCheck local fallback in tests
 * and the no-dedup branch in shape-only call sites) get a no-op true on
 * every call.
 */
export function shouldWarn(
  key: string,
  warnedAt: WarnedAt | undefined,
  now: () => number
): boolean {
  if (!warnedAt) return true;
  const last = warnedAt.get(key);
  const t = now();
  if (last !== undefined && t - last < WARN_REEMIT_INTERVAL_MS) {
    return false;
  }
  warnedAt.set(key, t);
  return true;
}

const DEFAULT_JWKS_TTL_MS = 60 * 60 * 1000;
/**
 * Failure-mode TTL for JWKS-unreachable entries. Pre-fix the failure
 * entry inherited the 1hr success TTL, so a single transient blip
 * locked pass-through mode for a full hour. 60s is short enough that
 * the next minute's request triggers a real refetch while still
 * suppressing the per-request fetch storm a 0s TTL would cause.
 */
const FAILURE_JWKS_TTL_MS = 60 * 1000;

/**
 * Stall bound for the JWKS / discovery reads specifically, well under
 * `proxyAwareFetch`'s 300 s default.
 *
 * Those reads are small documents, and — unlike the layer ZIP, which is up to
 * 250 MB and wants the long default — one of them sits on a PER-REQUEST path:
 * `agentcore-serve-auth`'s `buildAgentCoreServeAuthCheck` calls
 * `verifyJwtViaDiscovery` for every inbound request, and that function holds
 * no discovery cache. Behind a black-holed proxy each in-flight request would
 * otherwise hold a socket for 300 s where the pre-#647 direct read gave up in
 * about undici's 10 s connect timeout.
 *
 * Failing FAST is also the right bias here in a way it is not for a download:
 * an unreachable JWKS lands in the documented pass-through fallback
 * (`docs/cli-reference.md`), so a long hold buys nothing and costs a stuck
 * request.
 */
const JWKS_FETCH_TIMEOUT_MS = 10_000;

export function createJwksCache(
  opts: {
    fetchImpl?: (
      url: string
    ) => Promise<{ ok: boolean; status: number; text: () => Promise<string> }>;
    now?: () => number;
    ttlMs?: number;
    /** Failure-mode TTL override (defaults to {@link FAILURE_JWKS_TTL_MS}). */
    failureTtlMs?: number;
  } = {}
): JwksCache {
  // `proxyAwareFetch`, not the global: the JWKS endpoint is a REMOTE AWS
  // host (`cognito-idp.<region>.amazonaws.com` for a user-pool authorizer),
  // and the global `fetch` reads no proxy variable — so behind proxy-only
  // egress this read went direct while every SDK call tunneled (issue #647).
  const fetchImpl =
    opts.fetchImpl ?? ((url: string) => proxyAwareFetch(url, { timeoutMs: JWKS_FETCH_TIMEOUT_MS }));
  const now = opts.now ?? ((): number => Date.now());
  const ttlMs = opts.ttlMs ?? DEFAULT_JWKS_TTL_MS;
  const failureTtlMs = opts.failureTtlMs ?? FAILURE_JWKS_TTL_MS;
  const map = new Map<string, JwksCacheEntry>();

  return {
    async fetchAndCache(jwksUrl) {
      const cached = map.get(jwksUrl);
      if (cached && cached.expiresAt > now()) return cached;
      const logger = getLogger().child('cognito-jwt');
      try {
        const response = await fetchImpl(jwksUrl);
        if (!response.ok) {
          throw new Error(`JWKS fetch returned HTTP ${response.status}`);
        }
        const body = await response.text();
        const parsed = JSON.parse(body) as { keys?: unknown };
        const keys = Array.isArray(parsed.keys) ? parsed.keys : [];
        const byKid = new Map<string, JwksKey>();
        for (const k of keys) {
          if (!k || typeof k !== 'object' || Array.isArray(k)) continue;
          const obj = k as Record<string, unknown>;
          if (
            typeof obj['kid'] === 'string' &&
            typeof obj['n'] === 'string' &&
            typeof obj['e'] === 'string' &&
            typeof obj['kty'] === 'string'
          ) {
            byKid.set(obj['kid'], {
              kid: obj['kid'],
              n: obj['n'],
              e: obj['e'],
              kty: obj['kty'],
              ...(typeof obj['alg'] === 'string' && { alg: obj['alg'] }),
              ...(typeof obj['use'] === 'string' && { use: obj['use'] }),
            });
          }
        }
        const entry: JwksCacheEntry = {
          byKid,
          expiresAt: now() + ttlMs,
          passThrough: false,
        };
        map.set(jwksUrl, entry);
        return entry;
      } catch (err) {
        // Flattened + capped since issue #647: the read goes through
        // `proxyAwareFetch`, so this `catch` sees `node:net` / OpenSSL /
        // proxy-agent text of arbitrary shape where it used to see undici's
        // fixed `fetch failed`. This line is default-level and `cdkl studio`
        // mirrors it into a log ring it serves over HTTP, so it has to be ONE
        // bounded line. Nothing here needs withholding — no credential chain
        // is resolved for a JWKS GET.
        logger.warn(
          `JWKS unreachable at ${jwksUrl}: ${sanitizeServiceExceptionMessage(
            err instanceof Error ? err.message : String(err)
          )}. ` +
            `JWT validation will allow all tokens — local dev fallback. Configure network access to the JWKS URL ` +
            `to enable real signature verification.`
        );
        // Short-TTL failure entry so a transient blip doesn't lock
        // pass-through mode for a full hour. The next minute's request
        // re-attempts the fetch.
        const entry: JwksCacheEntry = {
          byKid: new Map(),
          expiresAt: now() + failureTtlMs,
          passThrough: true,
        };
        map.set(jwksUrl, entry);
        return entry;
      }
    },
    peek(jwksUrl) {
      return map.get(jwksUrl);
    },
    clear() {
      map.clear();
    },
  };
}

/**
 * Build the JWKS URL for a Cognito User Pool.
 *
 * Format: `https://cognito-idp.<region>.amazonaws.com/<userPoolId>/.well-known/jwks.json`
 */
export function buildCognitoJwksUrl(region: string, userPoolId: string): string {
  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}/.well-known/jwks.json`;
}

/**
 * Build the JWKS URL for a generic OIDC issuer (HTTP v2 JWT authorizers).
 * Trailing slash is normalized.
 */
export function buildJwksUrlFromIssuer(issuer: string): string {
  const stripped = issuer.replace(/\/+$/, '');
  return `${stripped}/.well-known/jwks.json`;
}

/**
 * Build the expected `iss` claim URL for a Cognito user pool. Matches the
 * issuer Cognito embeds in every minted JWT.
 */
function buildCognitoIssuer(region: string, userPoolId: string): string {
  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;
}

/**
 * Verify a Bearer JWT against the Cognito user pool(s) referenced by the
 * authorizer. With a multi-pool authorizer (`ProviderARNs[]` of length
 * 1+) the request-time pool selection is driven by the JWT's unverified
 * `iss` claim — only the matching pool's JWKS is used for signature
 * verification, so a token issued by pool A cannot be verified against
 * pool B's keys. Issuer mismatch against EVERY configured pool rejects
 * with 401.
 *
 * Returns `{ allow: false }` on:
 *   - missing / malformed Authorization header (caller surfaces 401);
 *   - signature verification failure;
 *   - expired token (`exp` in the past);
 *   - issuer mismatch (token's `iss` doesn't match any configured pool);
 *   - audience mismatch (token's `aud` not in the configured allowlist).
 *
 * Returns `{ allow: true, principalId, context }` on:
 *   - successful verification against the matching pool;
 *   - JWKS-unreachable pass-through mode for the matching pool (with a
 *     warn line on first hit; per-pool TTL handled by the cache).
 *
 * Backward compat: a single-element `ProviderARNs[]` (the historical
 * single-pool case) behaves identically to pre-PR — `pools[0]` is the
 * only candidate and the `iss` check matches it.
 */
export async function verifyCognitoJwt(
  authorizer: CognitoUserPoolAuthorizer,
  authorizationHeader: string | undefined,
  jwksCache: JwksCache,
  opts: { now?: () => number; warnedAt?: WarnedAt } = {}
): Promise<CachedAuthorizerResult & { identityHash: string | undefined; ttlSeconds: number }> {
  const now = opts.now ?? ((): number => Date.now());
  const token = extractBearer(authorizationHeader);
  if (!token) {
    return { allow: false, identityHash: undefined, ttlSeconds: 0 };
  }
  // `pools` is the canonical list; older callers that build the
  // authorizer literal (tests) may still pass legacy single-pool shape
  // — fall back to the legacy fields in that case.
  const pools =
    authorizer.pools && authorizer.pools.length > 0
      ? authorizer.pools
      : [
          {
            userPoolArn: authorizer.userPoolArn,
            region: authorizer.region,
            userPoolId: authorizer.userPoolId,
          },
        ];
  // Parse the token first so we can read its (unverified) `iss` claim
  // and pick the matching pool. parseJwt() failure is mapped to deny so
  // a garbage token never reaches the JWKS fetcher in the real-pool
  // (non pass-through) path — but the pass-through pool path needs to
  // accept malformed tokens, which the verify helper handles below.
  const parsed = parseJwt(token);
  const identityHash = buildIdentityHash([token]);

  // Pick the pool whose expected issuer URL matches the token's `iss`.
  // Strip trailing slashes defensively (a real Cognito token issuer
  // never has one, but be tolerant).
  let selectedPool = pools[0]!;
  let issMatched = false;
  if (parsed && typeof parsed.payload['iss'] === 'string') {
    const tokenIss = parsed.payload['iss'].replace(/\/+$/, '');
    for (const pool of pools) {
      if (buildCognitoIssuer(pool.region, pool.userPoolId) === tokenIss) {
        selectedPool = pool;
        issMatched = true;
        break;
      }
    }
    // Issuer mismatch against EVERY configured pool: reject without
    // touching JWKS. Multi-pool federation must NOT accept a token
    // whose issuer was never registered.
    if (!issMatched) {
      return { allow: false, identityHash, ttlSeconds: 0 };
    }
  } else if (pools.length > 1) {
    // Multi-pool federation safety: when there is more than one
    // configured pool AND the token did not parse (or has no string
    // `iss`), we have no safe way to route the request to a pool. The
    // pass-through fallback would arbitrarily pick pools[0] and, if
    // pool[0] happens to be in JWKS-pass-through mode, accept the
    // garbage token as `principalId: 'unknown'` — bypassing the
    // configured-issuer guard. Reject unconditionally instead.
    return { allow: false, identityHash, ttlSeconds: 0 };
  }
  // Single-pool case OR no-iss with one pool: fall through to
  // verifyAndShape against pools[0]. parseJwt-failure deny is enforced
  // there; if the pool is in pass-through mode, the malformed token
  // gets the "unknown" principal allow path (preserves PR 8b's design
  // intent that JWKS-unreachable accepts any Bearer token for that
  // single configured pool).
  const jwksUrl = buildCognitoJwksUrl(selectedPool.region, selectedPool.userPoolId);
  const expectedIssuer = buildCognitoIssuer(selectedPool.region, selectedPool.userPoolId);
  return verifyAndShape(
    token,
    jwksUrl,
    expectedIssuer,
    undefined,
    undefined,
    undefined,
    jwksCache,
    opts.warnedAt,
    now
  );
}

/**
 * Verify a Bearer JWT against an HTTP v2 JWT authorizer's `JwtConfiguration`.
 */
export async function verifyJwtAuthorizer(
  authorizer: JwtAuthorizer,
  authorizationHeader: string | undefined,
  jwksCache: JwksCache,
  opts: { now?: () => number; warnedAt?: WarnedAt } = {}
): Promise<CachedAuthorizerResult & { identityHash: string | undefined; ttlSeconds: number }> {
  const now = opts.now ?? ((): number => Date.now());
  const token = extractBearer(authorizationHeader);
  if (!token) {
    return { allow: false, identityHash: undefined, ttlSeconds: 0 };
  }
  // Cognito-issued JWTs let us hit the canonical JWKS URL directly. Other
  // issuers use OIDC discovery convention (`<issuer>/.well-known/jwks.json`).
  const jwksUrl =
    authorizer.region && authorizer.userPoolId
      ? buildCognitoJwksUrl(authorizer.region, authorizer.userPoolId)
      : buildJwksUrlFromIssuer(authorizer.issuer);
  return verifyAndShape(
    token,
    jwksUrl,
    authorizer.issuer.replace(/\/+$/, ''),
    authorizer.audience,
    undefined,
    undefined,
    jwksCache,
    opts.warnedAt,
    now
  );
}

/**
 * Inbound custom-JWT authorizer fronted by an OIDC discovery URL — the
 * shape Bedrock AgentCore Runtime declares
 * (`AuthorizerConfiguration.CustomJWTAuthorizer`).
 */
export interface DiscoveryJwtAuthorizer {
  /** OIDC discovery document URL (`.well-known/openid-configuration`). */
  discoveryUrl: string;
  /** Allowed `aud` values. */
  allowedAudience?: readonly string[];
  /** Allowed `client_id` values. */
  allowedClients?: readonly string[];
  /**
   * OAuth scopes the token's `scope` claim must include (space-separated
   * scope strings; the token must carry every entry in this allowlist).
   */
  allowedScopes?: readonly string[];
  /** Per-claim equality / membership rules the token must satisfy. */
  customClaims?: readonly JwtCustomClaim[];
}

/**
 * A single `CustomJWTAuthorizer.CustomClaims[]` rule the verifier evaluates
 * against the token's claims. Mirrors {@link AgentCoreCustomClaim} in the
 * resolver — kept structurally identical so the resolver -> verifier hand-off
 * is a direct field copy.
 */
export interface JwtCustomClaim {
  name: string;
  valueType: 'STRING' | 'STRING_ARRAY';
  operator: 'EQUALS' | 'CONTAINS' | 'CONTAINS_ANY';
  value: string | string[];
}

/**
 * Verify a Bearer JWT against an OIDC-discovery-URL authorizer (Bedrock
 * AgentCore Runtime's `customJwtAuthorizer`).
 *
 * Fetches the discovery document to learn the `issuer` + `jwks_uri`, then
 * delegates to {@link verifyAndShape} for the RS256 signature + `iss` +
 * `exp` + audience checks. The audience allowlist is
 * `allowedAudience ∪ allowedClients`, matched against the token's `aud`
 * (ID tokens) or `client_id` (access tokens).
 *
 * Failure modes mirror the JWKS-unreachable behavior: an absent / malformed
 * Bearer token is denied, but an unreachable / malformed discovery document
 * falls back to pass-through (accept + warn) so offline local dev still
 * works — the same trade-off `cdkl start-api` makes for unreachable JWKS.
 */
export async function verifyJwtViaDiscovery(
  authorizer: DiscoveryJwtAuthorizer,
  authorizationHeader: string | undefined,
  jwksCache: JwksCache,
  opts: {
    now?: () => number;
    warnedAt?: WarnedAt;
    fetchImpl?: (
      url: string
    ) => Promise<{ ok: boolean; status: number; text: () => Promise<string> }>;
  } = {}
): Promise<CachedAuthorizerResult & { identityHash: string | undefined; ttlSeconds: number }> {
  const now = opts.now ?? ((): number => Date.now());
  const token = extractBearer(authorizationHeader);
  if (!token) {
    return { allow: false, identityHash: undefined, ttlSeconds: 0 };
  }
  // Proxy-aware for the same reason as the JWKS read above (issue #647):
  // the discovery document is fetched from the IdP over the network, which
  // behind proxy-only egress is reachable only through the proxy.
  const fetchImpl =
    opts.fetchImpl ?? ((url: string) => proxyAwareFetch(url, { timeoutMs: JWKS_FETCH_TIMEOUT_MS }));

  let issuer: string;
  let jwksUri: string;
  try {
    const response = await fetchImpl(authorizer.discoveryUrl);
    if (!response.ok) {
      throw new Error(`discovery fetch returned HTTP ${response.status}`);
    }
    const doc = JSON.parse(await response.text()) as { issuer?: unknown; jwks_uri?: unknown };
    if (typeof doc.issuer !== 'string' || typeof doc.jwks_uri !== 'string') {
      throw new Error('discovery document missing issuer / jwks_uri');
    }
    issuer = doc.issuer;
    jwksUri = doc.jwks_uri;
  } catch (err) {
    // Discovery unreachable / malformed → pass-through accept (offline dev),
    // mirroring the JWKS-unreachable fallback in `createJwksCache`. The
    // warn re-emits every {@link WARN_REEMIT_INTERVAL_MS} (#247) so a
    // long-running `--watch` session keeps surfacing the degraded state
    // instead of silently accepting tokens after the first warn.
    if (shouldWarn(authorizer.discoveryUrl, opts.warnedAt, now)) {
      getLogger()
        .child('cognito-jwt')
        .warn(
          // One bounded line, for the same reason as the JWKS warn above.
          `OIDC discovery unreachable at ${authorizer.discoveryUrl}: ${sanitizeServiceExceptionMessage(
            err instanceof Error ? err.message : String(err)
          )}. Token accepted without verification — local dev fallback.`
        );
    }
    const identityHash = buildIdentityHash([token]);
    const parsed = parseJwt(token);
    if (parsed) return shapeAllowResult(parsed, identityHash, now);
    return { allow: true, principalId: 'unknown', context: {}, identityHash, ttlSeconds: 0 };
  }

  const allowlist = [...(authorizer.allowedAudience ?? []), ...(authorizer.allowedClients ?? [])];
  return verifyAndShape(
    token,
    jwksUri,
    issuer.replace(/\/+$/, ''),
    allowlist.length > 0 ? allowlist : undefined,
    authorizer.allowedScopes,
    authorizer.customClaims,
    jwksCache,
    opts.warnedAt,
    now
  );
}

async function verifyAndShape(
  token: string,
  jwksUrl: string,
  expectedIssuer: string,
  expectedAudience: ReadonlyArray<string> | undefined,
  requiredScopes: ReadonlyArray<string> | undefined,
  customClaims: ReadonlyArray<JwtCustomClaim> | undefined,
  jwksCache: JwksCache,
  warnedAt: WarnedAt | undefined,
  now: () => number
): Promise<CachedAuthorizerResult & { identityHash: string | undefined; ttlSeconds: number }> {
  const identityHash = buildIdentityHash([token]);

  // Fetch JWKS first so the pass-through mode (JWKS unreachable) can
  // accept every Bearer token — including malformed / non-JWT garbage —
  // without needing to first parse the token. Pre-fix the parseJwt
  // check above the JWKS fetch denied malformed tokens even in
  // pass-through mode, contradicting the design intent ("every JWT
  // accepted as if valid" → "every Bearer token accepted").
  const jwks = await jwksCache.fetchAndCache(jwksUrl);

  if (jwks.passThrough) {
    // Pre-#247 the warn was gated by a `Set<string>` URL set — once warned
    // for a URL, never again for the rest of the session. A long-running
    // `--watch` session that lost network briefly at boot silently accepted
    // every JWT for the rest of the afternoon. Post-#247 the warn re-emits
    // every {@link WARN_REEMIT_INTERVAL_MS}.
    if (shouldWarn(jwksUrl, warnedAt, now)) {
      getLogger()
        .child('cognito-jwt')
        .warn(
          `JWKS pass-through mode for ${jwksUrl}: token accepted without signature verification.`
        );
    }
    // Best-effort parse: a real JWT lets us still surface claims to the
    // handler. A malformed token gets a synthetic `unknown` principal
    // and an empty claims map. Either way the request is allowed.
    const parsed = parseJwt(token);
    if (parsed) {
      return shapeAllowResult(parsed, identityHash, now);
    }
    return {
      allow: true,
      principalId: 'unknown',
      context: {},
      identityHash,
      ttlSeconds: 0,
    };
  }

  const parsed = parseJwt(token);
  if (!parsed) {
    return { allow: false, identityHash, ttlSeconds: 0 };
  }

  const kid = parsed.header['kid'];
  if (typeof kid !== 'string') {
    return { allow: false, identityHash, ttlSeconds: 0 };
  }
  const key = jwks.byKid.get(kid);
  if (!key) {
    return { allow: false, identityHash, ttlSeconds: 0 };
  }

  if (!verifyRs256(token, key)) {
    return { allow: false, identityHash, ttlSeconds: 0 };
  }

  // Validate `exp`.
  const claims = parsed.payload;
  if (typeof claims['exp'] !== 'number' || claims['exp'] * 1000 <= now()) {
    return { allow: false, identityHash, ttlSeconds: 0 };
  }
  // Validate `iss` (best-effort — strip any trailing slash on either side).
  if (typeof claims['iss'] !== 'string' || claims['iss'].replace(/\/+$/, '') !== expectedIssuer) {
    return { allow: false, identityHash, ttlSeconds: 0 };
  }
  // Validate `aud` / `client_id`. Cognito access tokens use `client_id`,
  // ID tokens use `aud`. We accept whichever matches the allowlist.
  if (expectedAudience && expectedAudience.length > 0) {
    const aud = claims['aud'];
    const clientId = claims['client_id'];
    const audValues = Array.isArray(aud) ? aud : aud !== undefined ? [aud] : [];
    const matches =
      audValues.some((v) => typeof v === 'string' && expectedAudience.includes(v)) ||
      (typeof clientId === 'string' && expectedAudience.includes(clientId));
    if (!matches) {
      return { allow: false, identityHash, ttlSeconds: 0 };
    }
  }

  // Validate `scope`. AgentCore's `AllowedScopes` is a required-scope
  // allowlist: the token's `scope` claim (OAuth space-separated) must include
  // every entry. A token with no `scope` claim fails when scopes are required.
  if (requiredScopes && requiredScopes.length > 0) {
    if (!verifyRequiredScopes(claims['scope'], requiredScopes)) {
      return { allow: false, identityHash, ttlSeconds: 0 };
    }
  }

  // Validate custom claims. Every rule must hold against the token's
  // matching claim value; a token missing a referenced claim fails.
  if (customClaims && customClaims.length > 0) {
    for (const rule of customClaims) {
      if (!verifyCustomClaim(claims[rule.name], rule)) {
        return { allow: false, identityHash, ttlSeconds: 0 };
      }
    }
  }

  return shapeAllowResult(parsed, identityHash, now);
}

/**
 * The OAuth `scope` claim is a space-separated string. The token is allowed
 * iff every required scope is present (allowlist as REQUIRED, not OR).
 */
function verifyRequiredScopes(scopeClaim: unknown, requiredScopes: ReadonlyArray<string>): boolean {
  const tokenScopes =
    typeof scopeClaim === 'string'
      ? scopeClaim.split(/\s+/).filter((s) => s.length > 0)
      : Array.isArray(scopeClaim)
        ? scopeClaim.filter((s): s is string => typeof s === 'string')
        : [];
  return requiredScopes.every((s) => tokenScopes.includes(s));
}

/**
 * Verify a single `CustomJWTAuthorizer.CustomClaims` rule against the token's
 * claim value:
 *
 * - `STRING` + `EQUALS` — claim is a string equal to `value`.
 * - `STRING_ARRAY` + `CONTAINS` — claim is an array containing `value`.
 * - `STRING_ARRAY` + `CONTAINS_ANY` — claim is an array sharing at least one
 *   entry with `value` (an array of allowed strings).
 *
 * A missing or wrong-typed claim fails the rule.
 */
function verifyCustomClaim(claimValue: unknown, rule: JwtCustomClaim): boolean {
  if (rule.valueType === 'STRING') {
    if (rule.operator !== 'EQUALS' || typeof rule.value !== 'string') return false;
    return typeof claimValue === 'string' && claimValue === rule.value;
  }
  // STRING_ARRAY
  if (!Array.isArray(claimValue)) return false;
  const tokenValues = claimValue.filter((v): v is string => typeof v === 'string');
  if (rule.operator === 'CONTAINS') {
    if (typeof rule.value !== 'string') return false;
    return tokenValues.includes(rule.value);
  }
  if (rule.operator === 'CONTAINS_ANY') {
    if (!Array.isArray(rule.value)) return false;
    return rule.value.some((v) => tokenValues.includes(v));
  }
  return false;
}

/**
 * Construct the Allow result for a verified JWT. The handler-side context
 * is the parsed claim map; principalId mirrors Cognito's deployed
 * behavior (the `sub` claim, falling back to `username` then `cognito:username`).
 */
function shapeAllowResult(
  parsed: ParsedJwt,
  identityHash: string,
  now: () => number
): CachedAuthorizerResult & { identityHash: string; ttlSeconds: number } {
  const claims = parsed.payload;
  const principalId =
    pickStringClaim(claims, 'sub') ??
    pickStringClaim(claims, 'cognito:username') ??
    pickStringClaim(claims, 'username') ??
    'unknown';
  // Cap TTL at min(remaining-exp, 300s). The local server shouldn't
  // outlive a real JWT; cdk-local caches modestly to avoid spamming the
  // signature verifier on every request.
  const expMs = typeof claims['exp'] === 'number' ? claims['exp'] * 1000 : 0;
  const remainingSeconds = Math.max(0, Math.floor((expMs - now()) / 1000));
  const ttlSeconds = Math.min(300, remainingSeconds);
  return {
    allow: true,
    principalId,
    context: claims,
    identityHash,
    ttlSeconds,
  };
}

function pickStringClaim(claims: Record<string, unknown>, key: string): string | undefined {
  const v = claims[key];
  return typeof v === 'string' ? v : undefined;
}

/**
 * Parse `Authorization: Bearer <token>` into the bare token. Whitespace
 * around `Bearer` is tolerated; case is matched leniently. The token
 * itself is constrained to the JWT character class `[A-Za-z0-9._-]+`
 * (base64url alphabet plus the JWT `.` separator), so embedded
 * whitespace / quotes / other garbage in the header rejects rather than
 * being passed along to the JWT parser as a token containing spaces.
 * Returns `undefined` when the header is missing, the scheme is wrong,
 * or the token doesn't look JWT-shaped.
 */
function extractBearer(header: string | undefined): string | undefined {
  if (!header) return undefined;
  const m = /^\s*Bearer\s+([A-Za-z0-9._-]+)\s*$/i.exec(header);
  if (!m) return undefined;
  return m[1]!;
}

interface ParsedJwt {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  signingInput: string; // `<headerB64>.<payloadB64>`
  signatureB64: string;
}

/**
 * Parse a JWT into `(header, payload, signingInput, signatureB64)`.
 * Returns undefined on malformed input — the caller maps that to deny.
 */
function parseJwt(token: string): ParsedJwt | undefined {
  const parts = token.split('.');
  if (parts.length !== 3) return undefined;
  try {
    const headerJson = base64UrlDecodeToString(parts[0]!);
    const payloadJson = base64UrlDecodeToString(parts[1]!);
    const header = JSON.parse(headerJson) as Record<string, unknown>;
    const payload = JSON.parse(payloadJson) as Record<string, unknown>;
    return {
      header,
      payload,
      signingInput: `${parts[0]}.${parts[1]}`,
      signatureB64: parts[2]!,
    };
  } catch {
    return undefined;
  }
}

/**
 * Verify an RS256 JWT signature against an RSA public key derived from
 * the JWKS entry. Uses Node's `crypto.createPublicKey({key, format: 'jwk'})`
 * — Node 16+ understands the JWK format directly.
 */
function verifyRs256(token: string, key: JwksKey): boolean {
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  const signingInput = `${parts[0]}.${parts[1]}`;
  const signature = base64UrlDecodeToBuffer(parts[2]!);
  try {
    // Node 16+ accepts the JWK shape via `format: 'jwk'`. Cast the
    // input through `unknown` so we don't need the DOM-side `JsonWebKey`
    // type at compile time.
    const publicKey = createPublicKey({
      key: { kty: key.kty, n: key.n, e: key.e },
      format: 'jwk',
    } as unknown as Parameters<typeof createPublicKey>[0]);
    const verifier = createVerify('RSA-SHA256');
    verifier.update(signingInput);
    verifier.end();
    return verifier.verify(publicKey, signature);
  } catch {
    return false;
  }
}

function base64UrlDecodeToString(input: string): string {
  return base64UrlDecodeToBuffer(input).toString('utf-8');
}

function base64UrlDecodeToBuffer(input: string): Buffer {
  // Add padding back: base64url strips `=` padding.
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  return Buffer.from(padded + padding, 'base64');
}
